
import React, { useState, useEffect } from 'react';
import { Share2, Copy, Send, Loader2, RefreshCw, Camera, LogOut, PlusCircle, Mail, Phone, Crown, ShieldCheck, Clock, Sparkles, CreditCard, CheckCircle2, Settings, Lock, CalendarClock, Edit3, Save, KeyRound, User } from 'lucide-react';
import { Project, UserSubscription, PlanTier } from '../types';
import { updateUserProject, getUserSubscription, getUserProfile, saveUserProfile, UserProfile } from '../services/databaseService';
import { auth } from '../services/firebase';
import { updateProfile, sendPasswordResetEmail } from '@firebase/auth';
import { createPortalSession } from '../services/stripeService';
import { PricingModal } from './PricingModal';
import { STRIPE_PLANS, APP_CONFIG } from '../constants';

interface SettingsViewProps {
  project: Project;
  setProject: React.Dispatch<React.SetStateAction<Project>>;
  onLogout: () => void;
  onCreateProject: () => void;
  onDeleteProject: (projectId: string) => void;
  planTier: PlanTier;
  onUpgrade: () => void;
  projectsCount: number;
}

const PLAN_CONFIG = {
  trial: {
    label: "Plano Gratuito",
    sublabel: "Limite de 1 Obra",
    colors: "bg-slate-100 text-slate-600 border-slate-200",
    icon: Clock,
    features: "Funcionalidades Básicas"
  },
  premium: {
    label: "Premium", 
    sublabel: "Assinante Ativo",
    colors: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: Crown,
    features: "Gestão Completa"
  },
  pro_plus: {
    label: "PRO+",
    sublabel: "Membro VIP",
    colors: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icon: Sparkles,
    features: "Acesso Ilimitado"
  }
};

export const SettingsView: React.FC<SettingsViewProps> = ({ project, setProject, onLogout, onCreateProject, onDeleteProject, planTier, onUpgrade, projectsCount }) => {
  const [generating, setGenerating] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({ displayName: '', phone: '' });
  const [infoMessage, setInfoMessage] = useState("");
  const [loadingSub, setLoadingSub] = useState(true);
  
  const user = auth.currentUser;
  
  const maskPhone = (value: string) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    const limited = digits.substring(0, 11);
    if (limited.length <= 2) return limited.length > 0 ? `(${limited}` : "";
    if (limited.length <= 3) return `(${limited.substring(0, 2)}) ${limited.substring(2)}`;
    if (limited.length <= 7) return `(${limited.substring(0, 2)}) ${limited.substring(2, 3)} ${limited.substring(3)}`;
    if (limited.length <= 11) return `(${limited.substring(0, 2)}) ${limited.substring(2, 3)} ${limited.substring(3, 7)}-${limited.substring(7)}`;
    return value;
  };

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        const [sub, profile] = await Promise.all([
          getUserSubscription(user.uid),
          getUserProfile(user.uid)
        ]);
        setSubscription(sub);
        setUserProfile(profile);
        if (profile) {
          setProfileFormData({
            displayName: profile.displayName || user.displayName || '',
            phone: profile.phone || ''
          });
        } else {
          setProfileFormData({
            displayName: user.displayName || '',
            phone: ''
          });
        }
      }
      setLoadingSub(false);
    };
    loadData();
  }, [user]);

  const handleUpdateProfile = async () => {
    if (!user) return;
    setGenerating(true);
    try {
      await updateProfile(user, { displayName: profileFormData.displayName });
      await saveUserProfile(user.uid, profileFormData);
      setUserProfile(prev => prev ? { ...prev, ...profileFormData } : null);
      setIsEditingProfile(false);
      setInfoMessage("Perfil atualizado com sucesso!");
      setTimeout(() => setInfoMessage(""), 3000);
    } catch (err) {
      alert("Erro ao atualizar perfil.");
    } finally {
      setGenerating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setGenerating(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setInfoMessage("E-mail de redefinição enviado!");
      setTimeout(() => setInfoMessage(""), 3000);
    } catch (err) {
      alert("Erro ao enviar e-mail de redefinição.");
    } finally {
      setGenerating(false);
    }
  };

  const currentPlan = PLAN_CONFIG[planTier];
  const providerLink = project.photoToken ? `${window.location.origin}?token=${project.photoToken}` : null;
  const inviteLink = project.shareCode ? `${window.location.origin}?invite=${project.shareCode}` : null;

  const currentCollaborators = project.collaborators?.filter(uid => uid !== project.ownerId)?.length || 0;
  const collaboratorLimit = planTier === 'trial' ? 0 : (planTier === 'premium' ? 1 : 999);
  const canAddCollaborator = currentCollaborators < collaboratorLimit;
  const allowProviderLink = planTier === 'pro_plus';

  const handleShareProvider = async () => {
    let linkToShare = providerLink;
    if (!linkToShare) return;
    const shareText = `Olá! Aqui está o link para acessar o Diário de Obra e enviar fotos:\n\n${linkToShare}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Link do Prestador', text: shareText, url: linkToShare }); } catch (err) {}
    } else { window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank'); }
  };

  const handleShareInvite = async () => {
    if (!inviteLink) return;
    const shareText = `Use este código para acessar a obra no App Minha Obra: ${project.shareCode}\n\nOu clique no link: ${inviteLink}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Convite para Obra', text: shareText, url: inviteLink }); } catch (err) {}
    } else { navigator.clipboard.writeText(shareText); alert("Link e código copiados!"); }
  };

  const handlePortal = async () => {
    if (!subscription) { onUpgrade(); return; }
    setLoadingCheckout(true);
    try { await createPortalSession(); } catch (error) { setLoadingCheckout(false); }
  };

  const userInitials = (profileFormData.displayName || "U").substring(0, 2).toUpperCase();

  return (
    <div className="space-y-8 animate-fadeIn pb-24">
       <div className="flex justify-between items-center">
          <h2 className="text-3xl font-black uppercase tracking-tighter">Ajustes</h2>
       </div>

       {infoMessage && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold border border-emerald-100 animate-fadeIn">
            <CheckCircle2 size={18} /> {infoMessage}
          </div>
       )}

       {/* CARTÃO DE PERFIL COM EDIÇÃO */}
       <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl border border-slate-100 dark:border-slate-800 relative overflow-hidden group">
          <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-[80px] -mr-16 -mt-16 pointer-events-none opacity-50 transition-colors duration-500
            ${planTier === 'trial' ? 'bg-slate-400/10' : ''}
            ${planTier === 'premium' ? 'bg-emerald-400/20' : ''}
            ${planTier === 'pro_plus' ? 'bg-indigo-400/20' : ''}
          `}></div>

          <div className="relative z-10 p-8">
            <div className="flex flex-col md:flex-row items-start gap-8">
               <div className="flex items-center gap-6 w-full md:w-auto">
                   <div className={`size-24 rounded-3xl p-1 shadow-xl shrink-0 bg-gradient-to-br
                      ${planTier === 'trial' ? 'from-slate-200 to-slate-400' : ''}
                      ${planTier === 'premium' ? 'from-emerald-400 to-emerald-600' : ''}
                      ${planTier === 'pro_plus' ? 'from-indigo-400 to-indigo-600' : ''}
                   `}>
                      <div className="w-full h-full rounded-[1.3rem] bg-white dark:bg-slate-900 flex items-center justify-center">
                         <span className={`font-black text-3xl
                            ${planTier === 'trial' ? 'text-slate-500' : ''}
                            ${planTier === 'premium' ? 'text-emerald-600' : ''}
                            ${planTier === 'pro_plus' ? 'text-indigo-600' : ''}
                         `}>{userInitials}</span>
                      </div>
                   </div>

                   <div className="flex-1">
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          className="text-xl font-black bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-3 py-1 outline-none w-full" 
                          value={profileFormData.displayName} 
                          onChange={e => setProfileFormData({...profileFormData, displayName: e.target.value})}
                        />
                      ) : (
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none mb-2">{profileFormData.displayName}</h3>
                      )}
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${currentPlan.colors}`}>
                            <currentPlan.icon size={14} strokeWidth={2.5} />
                            <div className="flex flex-col leading-none">
                              <span className="text-[10px] font-black uppercase tracking-widest">
                                {currentPlan.label}
                              </span>
                            </div>
                      </div>
                   </div>
               </div>

               <div className="flex-1 w-full md:border-l border-slate-100 dark:border-slate-800 md:pl-8 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-2xl flex items-center gap-3">
                         <div className="size-8 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center text-slate-400 shadow-sm"><Mail size={14}/></div>
                         <div className="flex-1 min-w-0">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Email (Não editável)</p>
                            <p className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate">{user?.email}</p>
                         </div>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-2xl flex items-center gap-3">
                         <div className="size-8 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center text-slate-400 shadow-sm"><Phone size={14}/></div>
                         <div className="flex-1 min-w-0">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">WhatsApp</p>
                            {isEditingProfile ? (
                              <input 
                                type="tel" 
                                className="text-xs font-bold bg-white dark:bg-slate-800 border-none rounded-lg px-2 py-1 outline-none w-full" 
                                value={profileFormData.phone} 
                                onChange={e => setProfileFormData({...profileFormData, phone: maskPhone(e.target.value)})}
                              />
                            ) : (
                              <p className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate">{profileFormData.phone || 'Não informado'}</p>
                            )}
                         </div>
                      </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {isEditingProfile ? (
                      <button 
                        onClick={handleUpdateProfile} 
                        disabled={generating}
                        className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                      >
                        {generating ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar Dados
                      </button>
                    ) : (
                      <button 
                        onClick={() => setIsEditingProfile(true)}
                        className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                      >
                        <Edit3 size={14} /> Editar Perfil
                      </button>
                    )}
                    <button 
                      onClick={handleResetPassword} 
                      disabled={generating}
                      className="flex-1 py-3 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      <KeyRound size={14} /> Redefinir Senha
                    </button>
                  </div>
               </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
               <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {subscription ? (
                     <div className="text-[10px] text-emerald-600 font-bold flex items-center gap-2 bg-emerald-50 px-4 py-2 rounded-full"><CheckCircle2 size={14} /> Membro Assinante</div>
                  ) : (
                     <p className="text-xs text-slate-500 font-medium">Faça o upgrade para desbloquear colaboradores ilimitados e relatórios.</p>
                  )}
                  <button onClick={subscription ? handlePortal : onUpgrade} disabled={loadingCheckout} className={`w-full sm:w-auto px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${subscription ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-105 shadow-lg'}`}>
                     {loadingCheckout ? <Loader2 size={14} className="animate-spin" /> : (subscription ? <Settings size={14} /> : <CreditCard size={14} />)} 
                     {subscription ? 'Gerenciar Assinatura' : 'Assinar Agora'}
                  </button>
               </div>
            </div>
          </div>
       </div>
       
       {/* SEÇÃO DE COMPARTILHAMENTO */}
       <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-xl border border-slate-100 dark:border-slate-800 space-y-8">
          <div>
             <div className="flex items-center gap-2 mb-3">
                <Share2 size={16} className={canAddCollaborator ? "text-emerald-600" : "text-slate-400"} />
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Código de Acesso (Colaboradores)</p>
             </div>
             
             {project.shareCode ? (
               <>
                 <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 group relative overflow-hidden mb-4">
                    <span className="text-3xl font-black text-slate-900 dark:text-white tracking-[0.3em] font-mono">{project.shareCode}</span>
                    <button onClick={() => { navigator.clipboard.writeText(project.shareCode!); alert("Código copiado!"); }} className="ml-auto px-4 py-2 bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-300 transition-colors"><Copy size={14} /></button>
                 </div>
                 <button onClick={handleShareInvite} className="w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center gap-2 transition-colors"><Send size={16} /> Enviar Convite</button>
               </>
             ) : (
               <button onClick={onUpgrade} className="w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg bg-slate-900 text-white">
                 <Crown size={16} /> Fazer Upgrade para Colaboradores
               </button>
             )}
          </div>
       </div>

       {/* ZONA DE GERENCIAMENTO */}
       <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-xl border border-slate-100 dark:border-slate-800">
          <h3 className="text-lg font-black uppercase tracking-tight mb-6">Ações da Conta</h3>
          <div className="space-y-4">
            <button 
              type="button"
              onClick={projectsCount < APP_CONFIG.MAX_PRO_PROJECTS ? onCreateProject : onUpgrade} 
              className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2 ${projectsCount < (APP_CONFIG as any)[`MAX_${planTier.toUpperCase()}_PROJECTS`] ? 'bg-slate-100 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10' : 'bg-slate-900 text-white'}`}
            >
              {projectsCount < (APP_CONFIG as any)[`MAX_${planTier.toUpperCase()}_PROJECTS`] ? <PlusCircle size={16} /> : <Crown size={16} />}
              {projectsCount < (APP_CONFIG as any)[`MAX_${planTier.toUpperCase()}_PROJECTS`] ? 'Adicionar Nova Obra' : 'Liberar mais Obras'}
            </button>
            <button type="button" onClick={onLogout} className="w-full py-4 text-slate-500 bg-slate-50 dark:bg-slate-900/10 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-900/20 transition-colors flex items-center justify-center gap-2"><LogOut size={16} /> Sair da Conta</button>
          </div>
       </div>
    </div>
  );
};
